import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { Modal, Button, Spinner } from "react-bootstrap";
import { Icon } from "@iconify/react";
import { PDFViewer, pdf } from "@react-pdf/renderer";
import { Buffer } from "buffer";
import { toast } from "react-hot-toast";

import InvoicePDF from "./InvoicePDF";
import InvoicePDFA5 from "./InvoicePDFA5";
import QuoteInvoiceA4 from "./QuoteInvoiceA4";
import QuoteA5 from "./QuoteA5";
import InvoiceCoverPDF from "./InvoiceCoverPDF";
import { useButtonTableStyle } from "../utils/useButtonTableStyle";
import { getCurrentPinType } from "../pages/const";
import {
  listBankAccounts,
  listMetalGroups,
  listProductGroups,
  listSales,
  showSale,
} from "../erpClient";
import {
  normalizeListSalesRows,
  getLegacySaleDetailsFromShowResponse,
  isSalesApiSuccess,
} from "../utils/salesErpAdapter";
import {
  formatApiPaymentDisplayLabel,
  isUrdLinkedPaymentMode,
} from "../utils/paymentLinesErpAdapter";
import {
  resolveSaleEinvoiceFields,
  buildSignedQrDataUrl,
} from "../utils/einvoiceFields";
import { formatAmountInWords } from "../utils/numberToWords";

// Ensure Buffer is available
window.Buffer = Buffer;

let cachedProductGroups = null;
let cachedMetalGroups = null;
let cachedBankAccounts = null;

async function getProductGroups() {
  if (cachedProductGroups) return cachedProductGroups;
  try {
    const response = await listProductGroups();
    const productGroupsData =
      response?.success && Array.isArray(response.data?.product_groups)
        ? response.data.product_groups
        : [];
    cachedProductGroups = productGroupsData.map((group) => ({
      id: group.id,
      name: group.name || "",
      metalGroupId: group.metal_group_id,
      shortCode: group.short_code || "",
    }));
  } catch (e) {
    console.error("Error fetching product groups:", e);
    cachedProductGroups = [];
  }
  return cachedProductGroups;
}

async function getMetalGroups() {
  if (cachedMetalGroups) return cachedMetalGroups;
  try {
    const response = await listMetalGroups();
    const metalGroupsArray = Array.isArray(response?.data?.metal_groups)
      ? response.data.metal_groups
      : Array.isArray(response?.data?.records)
        ? response.data.records
        : [];
    const metalGroupsMap = {};
    if (Array.isArray(metalGroupsArray)) {
      metalGroupsArray.forEach((group) => {
        metalGroupsMap[group.id] = {
          purity: group.purity || "",
          metal: group.metal || "",
          HSN_code: group.HSN_code || "",
        };
      });
    }
    cachedMetalGroups = metalGroupsMap;
  } catch (e) {
    console.error("Error fetching metal groups:", e);
    cachedMetalGroups = {};
  }
  return cachedMetalGroups;
}

async function getBankAccounts() {
  if (cachedBankAccounts) return cachedBankAccounts;
  try {
    const response = await listBankAccounts();
    const rows = Array.isArray(response?.data)
      ? response.data
      : Array.isArray(response?.data?.data)
        ? response.data.data
        : [];
    const bankMapping = {};
    rows.forEach((b) => {
      bankMapping[b.id] = b.bank_name || b.name || "";
    });
    cachedBankAccounts = bankMapping;
  } catch (e) {
    console.error("Error fetching bank accounts:", e);
    cachedBankAccounts = {};
  }
  return cachedBankAccounts;
}

function formatDate(dateString) {
  if (!dateString) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(String(dateString))) {
    const [year, month, day] = String(dateString).split("-");
    return `${day}-${month}-${year}`;
  }
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return String(dateString);
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
}

const toNumberOrZero = (value) => {
  const numeric = parseFloat(value);
  return Number.isFinite(numeric) ? numeric : 0;
};

const normalizePdfCharge = (charge = {}) => ({
  ...charge,
  charge_type: charge.charge_type || charge.chargeType || charge.type || "",
  ct: charge.ct ?? charge.charge_ct ?? 0,
  wt: charge.wt ?? charge.weight ?? charge.wtTotal ?? charge.wt_total ?? 0,
  pcs: charge.pcs ?? charge.count ?? 0,
  rate_on: charge.rate_on || charge.rateOn || "",
  rate: charge.rate ?? 0,
  amount: charge.amount ?? charge.amt ?? 0,
});

const normalizePdfItem = (item = {}) => {
  const rawName = item.item_category_name || "";

  return {
    id: item.id ?? item.product_id ?? item.tag_no ?? "",
    name: rawName,
    issueDescription:
      item.issueDescription ||
      item.issue_description ||
      item.description ||
      "",
    purity: item.purity || item.metal_purity || item.product_purity || "",
    hsn: item.hsn || item.HSN_code || item.hsn_code || "7113",
    grossWeight:
      item.gwt ?? item.grossWeight ?? item.gross_weight ?? "0.0000",
    netWeight: item.nwt ?? item.netWeight ?? item.net_weight ?? "0.0000",
    rate: item.item_rate ?? item.itrate ?? item.rate ?? "0.00",
    othamt: item.oth_amt ?? item.othamt ?? item.other_amount ?? "0.00",
    amount:
      item.total_amount ??
      item.totalamt ??
      item.item_amount ??
      item.amount ??
      "0.00",
    charges: Array.isArray(item.charges)
      ? item.charges.map(normalizePdfCharge)
      : [],
    lbr_type: item.lbr_type || "",
    lbr_percent: item.lbr_percent || "0.00",
    lbr_rate: item.lbr_rate || "0.00",
    lbr_amt: item.lbr_amt || "0.00",
    carat: item.carat || "",
    tag_no: item.tag_no || item.tagNo || item.tag || "",
    huid: item.huid || "",
  };
};

const normalizePdfCustomer = (invoice = {}, user = {}) => ({
  name:
    invoice.customer_name ||
    invoice.name ||
    invoice.customerDetails?.name ||
    user.name ||
    "",
  mobile:
    invoice.customer_mobile ||
    invoice.mobile ||
    invoice.customerDetails?.mobile ||
    user.mobile ||
    "",
  address:
    invoice.customer_address ||
    invoice.address ||
    invoice.customerDetails?.address ||
    user.address ||
    "",
  city:
    invoice.customer_city ||
    invoice.city ||
    invoice.customerDetails?.city ||
    user.city ||
    "",
  state:
    invoice.customer_state ||
    invoice.state ||
    invoice.customerDetails?.state ||
    user.state ||
    "",
  GST_number:
    invoice.customer_gst_number ||
    invoice.gst_number ||
    invoice.customerDetails?.gst_number ||
    user.GST_number ||
    "",
  pan_no:
    invoice.customer_pan_no ||
    invoice.pan_no ||
    invoice.customerDetails?.pan_no ||
    user.pan_no ||
    "",
  aadhar_no:
    invoice.customer_aadhar_no ||
    invoice.aadhar_no ||
    invoice.customerDetails?.aadhar_no ||
    user.aadhar_no ||
    "",
});

const normalizePdfInvoice = (invoice = {}, user = {}, bankAccounts = {}) => {
  const items = Array.isArray(invoice.items)
    ? invoice.items
    : Array.isArray(invoice.sales)
      ? invoice.sales
      : [];
  const totalAmount = toNumberOrZero(
    invoice.total_amount ?? invoice.final_amount ?? invoice.totalAmount,
  );
  const grandTotal = toNumberOrZero(
    invoice.grand_total ?? invoice.grandTotal ?? invoice.total_amount,
  );
  const remainingAmount = toNumberOrZero(
    invoice.remaining_amount ?? invoice.remainingAmount,
  );
  const paidAmount = toNumberOrZero(
    invoice.paid_amount ?? invoice.paidAmount ?? grandTotal - remainingAmount,
  );
  const taxAmount = toNumberOrZero(
    invoice.tax_amount ?? invoice.gst_amount ?? invoice.totalTax,
  );
  const gstPercent = toNumberOrZero(invoice.gst_percent ?? 0);
  const sgst = toNumberOrZero(invoice.sgst ?? 0);
  const cgst = toNumberOrZero(invoice.cgst ?? 0);
  const igst = toNumberOrZero(invoice.igst ?? 0);
  const normalizedOldPurchase = Array.isArray(invoice.old_purchase)
    ? invoice.old_purchase
    : Array.isArray(invoice.old_purchase_details)
      ? invoice.old_purchase_details
      : [];
  const normalizedPayments = Array.isArray(invoice.payments)
    ? invoice.payments
    : [];

  return {
    invoiceNumber:
      invoice.doc_number ||
      invoice.voucherNumber ||
      invoice.voucher_number ||
      "",
    date: formatDate(
      invoice.doc_date || invoice.creationDate || invoice.date,
    ),
    customer: normalizePdfCustomer(invoice, user),
    items: items.map(normalizePdfItem),
    final_amount: totalAmount.toFixed(2),
    subtotal: totalAmount.toFixed(2),
    gst_percent: gstPercent.toFixed(2),
    sgst: sgst.toFixed(2),
    cgst: cgst.toFixed(2),
    igst: igst.toFixed(2),
    gst_amount: taxAmount.toFixed(2),
    totalTax: taxAmount.toFixed(2),
    grandTotal: grandTotal.toFixed(2),
    amountInWords: formatAmountInWords(grandTotal),
    round_off: (invoice.round_off ?? 0).toString(),
    note: invoice.notes || invoice.note || "",
    paidAmount: paidAmount.toFixed(2),
    remainingAmount:
      invoice.remaining_amount != null || invoice.remainingAmount != null
        ? remainingAmount.toFixed(2)
        : Math.max(0, grandTotal - paidAmount).toFixed(2),
    payments: normalizedPayments,
    old_purchase: normalizedOldPurchase,
    payable_amount: invoice.payable_amount ?? invoice.payableAmount ?? 0,
    sale_payable:
      invoice.sale_payable ?? invoice.salePayable ?? invoice.payables ?? [],
    followup_date: invoice.followup_date || "",
    discount: invoice.discount || "0.00",
    discount_amount: invoice.discount_amount ?? "0.00",
    bankAccounts: bankAccounts || {},
    signed_qr_image: invoice.signed_qr_image || "",
    ...resolveSaleEinvoiceFields(invoice),
    billed_to_name:
      invoice.billed_to_name ||
      invoice.billedTo?.name ||
      invoice.bill_to_name ||
      invoice.customerDetails?.billed_to_name ||
      "",
    billed_to_mobile:
      invoice.billed_to_mobile ||
      invoice.billedTo?.mobile ||
      invoice.customerDetails?.billed_to_mobile ||
      "",
    billed_to_aadhar_no:
      invoice.billed_to_aadhar_no ||
      invoice.billedTo?.aadhar_no ||
      invoice.customerDetails?.billed_to_aadhar_no ||
      "",
    billed_to_pan_no:
      invoice.billed_to_pan_no ||
      invoice.billedTo?.pan_no ||
      invoice.customerDetails?.billed_to_pan_no ||
      "",
    billed_to_address:
      invoice.billed_to_address ||
      invoice.billedTo?.address ||
      invoice.customerDetails?.billed_to_address ||
      "",
    billed_to_city:
      invoice.billed_to_city ||
      invoice.billedTo?.city ||
      invoice.customerDetails?.billed_to_city ||
      "",
    billed_to_state:
      invoice.billed_to_state ||
      invoice.billedTo?.state ||
      invoice.customerDetails?.billed_to_state ||
      "",
    billed_to_area:
      invoice.billed_to_area ||
      invoice.billedTo?.area ||
      invoice.customerDetails?.billed_to_area ||
      "",
    billed_to_gst_number:
      invoice.billed_to_gst_number ||
      invoice.billedTo?.gst_number ||
      invoice.billedTo?.GST_number ||
      invoice.customerDetails?.billed_to_gst_number ||
      "",
  };
};

export function extractSalesVoucherNumber(row) {
  if (!row) return "";
  if (row.rawEntry?.voucher_number) return String(row.rawEntry.voucher_number).trim();
  if (row.rawEntry?.voucher_no) return String(row.rawEntry.voucher_no).trim();
  if (row.rawEntry?.doc_number) return String(row.rawEntry.doc_number).trim();
  if (row.rawEntry?.invoice_no) return String(row.rawEntry.invoice_no).trim();

  const typeStr = String(row.type || "").trim();
  const typeMatch = typeStr.match(/sales?\s*[-:]\s*([A-Za-z0-9_-]+)/i);
  if (typeMatch && typeMatch[1]) return typeMatch[1].trim();

  const codeMatch = typeStr.match(/((?:SAL|QSAL)[_-][A-Za-z0-9_-]+)/i);
  if (codeMatch && codeMatch[1]) return codeMatch[1].trim();

  const remarksStr = String(row.remarks || "").trim();
  const remarkMatch = remarksStr.match(/((?:SAL|QSAL)[_-][A-Za-z0-9_-]+)/i);
  if (remarkMatch && remarkMatch[1]) return remarkMatch[1].trim();

  return "";
}

export function isSalesLedgerRow(row) {
  if (!row || row.isOpening) return false;
  const t = String(row.type || "").trim();
  const tLower = t.toLowerCase();
  // Payment on a sale ("CASH - SAL_0817") — open the SALES row, not this line
  if (
    /^(CASH|BANK|UPI|CARD|CHEQUE|OLD[_\s-]?GOLD|OLD[_\s-]?SILVER)\s*[-:]/i.test(
      t,
    ) &&
    /\b(SAL|QSAL)[_-]/i.test(t)
  ) {
    return false;
  }
  if (String(row.rawEntry?.entry_type || "").toLowerCase() === "sale_payment") {
    return false;
  }
  // Prefer explicit sales due / sales label
  if (tLower.startsWith("sale") || tLower.startsWith("sales")) return true;
  if (String(row.rawEntry?.entry_type || "").toLowerCase() === "sale_due") {
    return true;
  }
  if (row.rawEntry?.sale_id || row.rawEntry?.sales_id) {
    if (!/^(CASH|BANK|UPI|CARD|CHEQUE)\b/i.test(t)) return true;
  }
  if (
    row.rawEntry?.voucher_no &&
    String(row.rawEntry.voucher_no).toUpperCase().startsWith("SAL_")
  ) {
    if (tLower.startsWith("sale")) return true;
  }
  if (
    row.rawEntry?.voucher_number &&
    String(row.rawEntry.voucher_number).toUpperCase().startsWith("SAL_")
  ) {
    if (tLower.startsWith("sale")) return true;
  }
  return false;
}

export default function SalesInvoicePrintModal({
  show,
  onHide,
  saleRow,
  customerInfo,
}) {
  const { buttonClass } = useButtonTableStyle();
  const [loading, setLoading] = useState(false);
  const [preparedData, setPreparedData] = useState(null);
  const [showPdfSizeModal, setShowPdfSizeModal] = useState(false);
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [showCoverModal, setShowCoverModal] = useState(false);
  const [pdfPaperSize, setPdfPaperSize] = useState("A4");

  const handleCloseAll = useCallback(() => {
    setShowPdfSizeModal(false);
    setShowPdfModal(false);
    setShowCoverModal(false);
    setPreparedData(null);
    setPdfPaperSize("A4");
    if (onHide) onHide();
  }, [onHide]);

  // Load sale details whenever modal is shown
  useEffect(() => {
    if (!show || !saleRow) {
      return;
    }

    let isMounted = true;
    setLoading(true);
    setShowPdfSizeModal(true);

    const loadData = async () => {
      try {
        const [productGroups, metalGroups, bankAccounts] = await Promise.all([
          getProductGroups(),
          getMetalGroups(),
          getBankAccounts(),
        ]);

        const voucherNumber = extractSalesVoucherNumber(saleRow);
        // User ledger returns document_id for the sale; prefer that over legacy
        // sale_id / sales_id so we can call type:"show" without a list/mixed search.
        let saleId =
          saleRow.rawEntry?.document_id ||
          saleRow.rawEntry?.sale_id ||
          saleRow.rawEntry?.sales_id ||
          saleRow.rawEntry?.id;

        // Fallback only for older ledger shapes that lack document_id
        if (!saleId && voucherNumber) {
          try {
            const res = await listSales({ search: voucherNumber, type: "mixed" });
            if (isSalesApiSuccess(res)) {
              const rows = normalizeListSalesRows(res);
              const match =
                rows.find(
                  (r) =>
                    String(r.voucherNumber || "").trim().toUpperCase() ===
                      voucherNumber.toUpperCase() ||
                    String(r.doc_number || "").trim().toUpperCase() ===
                      voucherNumber.toUpperCase(),
                ) || rows[0];
              if (match?.id) saleId = match.id;
            }
          } catch (err) {
            console.warn("listSales mixed search error:", err);
          }
        }

        if (!saleId && voucherNumber) {
          try {
            const res = await listSales({ search: voucherNumber, type: "list" });
            if (isSalesApiSuccess(res)) {
              const rows = normalizeListSalesRows(res);
              const match =
                rows.find(
                  (r) =>
                    String(r.voucherNumber || "").trim().toUpperCase() ===
                      voucherNumber.toUpperCase() ||
                    String(r.doc_number || "").trim().toUpperCase() ===
                      voucherNumber.toUpperCase(),
                ) || rows[0];
              if (match?.id) saleId = match.id;
            }
          } catch (err) {
            console.warn("listSales regular search error:", err);
          }
        }

        if (!saleId) {
          throw new Error(
            `Could not find sale record for ${voucherNumber || saleRow.type || "this transaction"}`,
          );
        }

        const docHint =
          voucherNumber ||
          saleRow?.rawEntry?.doc_number ||
          saleRow?.type ||
          "";
        const isQuoteDoc = String(docHint).toUpperCase().includes("QSAL_");
        const isAccessDoc =
          String(docHint).toUpperCase().includes("SAL_") && !isQuoteDoc;
        const showRes = await showSale({
          id: saleId,
          isQuote: isQuoteDoc ? true : isAccessDoc ? false : undefined,
        });
        const saleDetails = getLegacySaleDetailsFromShowResponse(showRes);
        if (!saleDetails) {
          throw new Error("Failed to load sale details from server");
        }

        const processedItems = Array.isArray(saleDetails.sales)
          ? saleDetails.sales.map((it) => {
              const productGroup = productGroups.find(
                (pg) =>
                  pg.id === it.product_group ||
                  pg.id === it.product_group_id ||
                  pg.name === it.product_group,
              );
              const metalGroup = productGroup
                ? metalGroups[productGroup.metalGroupId]
                : null;
              return {
                ...it,
                item_category_name: it.item_category_name || "",
                carat: it.carat || "",
                productGroupName:
                  it.product_group ||
                  (productGroup ? productGroup.name : "N/A"),
                purity:
                  it.metal_purity ||
                  (metalGroup ? metalGroup.purity : "N/A"),
                metal:
                  it.metal_group_name ||
                  (metalGroup ? metalGroup.metal : "N/A"),
                hsn:
                  it.hsn ||
                  (metalGroup && metalGroup.HSN_code
                    ? metalGroup.HSN_code
                    : ""),
              };
            })
          : [];

        const processedPayments = Array.isArray(saleDetails.payments)
          ? saleDetails.payments.map((p) => {
              const isUrd =
                isUrdLinkedPaymentMode(p?.payment_mode) ||
                Number(p?.urd_document_id) > 0;
              return {
                ...p,
                bank_name: isUrd
                  ? formatApiPaymentDisplayLabel(p)
                  : p.bank_name || bankAccounts[p.bank_type] || "Unknown Bank",
              };
            })
          : [];

        const enriched = {
          ...saleDetails,
          id: saleId,
          items: processedItems,
          payments: processedPayments,
          voucherNumber:
            saleDetails.voucher_number ||
            saleDetails.doc_number ||
            voucherNumber,
          doc_number:
            saleDetails.doc_number ||
            saleDetails.voucher_number ||
            voucherNumber,
          creationDate: saleDetails.date || saleRow.date,
          userId: saleDetails.user_id || customerInfo?.value,
          final_amount: saleDetails.final_amount,
          grandTotal: saleDetails.grand_total,
          round_off: saleDetails.round_off,
          remainingAmount: saleDetails.remaining_amount,
          payable_amount: saleDetails.payable_amount ?? 0,
          sale_payable: saleDetails.sale_payable ?? [],
          discount: saleDetails.discount,
          discount_amount: saleDetails.discount_amount,
          note: saleDetails.note,
          followup_date: saleDetails.followup_date,
          gst_percent: saleDetails.gst_percent,
          gst_amount: saleDetails.gst_amount,
          sgst: saleDetails.sgst,
          cgst: saleDetails.cgst,
          igst: saleDetails.igst,
          old_purchase:
            saleDetails.old_purchase ||
            saleDetails.old_purchase_details ||
            [],
          customerDetails: {
            name:
              saleDetails.name ||
              customerInfo?.label ||
              customerInfo?.name ||
              "",
            mobile:
              saleDetails.mobile ||
              customerInfo?.mobile ||
              "",
            address:
              saleDetails.address ||
              customerInfo?.address ||
              "",
            city:
              saleDetails.city ||
              customerInfo?.city ||
              "",
            state: saleDetails.state || "0",
            gst_number: saleDetails.gst_number || "",
            pan_no: saleDetails.pan_no || "",
            aadhar_no: saleDetails.aadhar_no || "",
            ...saleDetails.customerDetails,
          },
        };

        const pdfInvoice = {
          ...enriched,
          ...resolveSaleEinvoiceFields(enriched),
        };

        if (pdfInvoice.signed_qr_code) {
          try {
            pdfInvoice.signed_qr_image = await buildSignedQrDataUrl(
              pdfInvoice.signed_qr_code,
            );
          } catch (qrErr) {
            console.error("QR generation failed:", qrErr);
          }
        }

        const user = {
          name:
            enriched.customerDetails?.name ||
            customerInfo?.label ||
            customerInfo?.name ||
            "N/A",
          mobile:
            enriched.customerDetails?.mobile ||
            customerInfo?.mobile ||
            "N/A",
          email: "",
          state: enriched.customerDetails?.state || "0",
          city:
            enriched.customerDetails?.city ||
            customerInfo?.city ||
            "",
          address:
            enriched.customerDetails?.address ||
            customerInfo?.address ||
            "",
          aadhar_no: enriched.customerDetails?.aadhar_no || "",
          pan_no: enriched.customerDetails?.pan_no || "",
          GST_number: enriched.customerDetails?.gst_number || "",
          company_name: "",
          company_address: "",
          reference: "",
          status: "1",
        };

        if (isMounted) {
          setPreparedData({
            invoice: pdfInvoice,
            user,
            bankAccounts,
          });
        }
      } catch (err) {
        console.error("Error preparing sale invoice:", err);
        if (isMounted) {
          toast.error(err.message || "Failed to load sale details");
          handleCloseAll();
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadData();

    return () => {
      isMounted = false;
    };
  }, [show, saleRow, customerInfo, handleCloseAll]);

  const handlePdfSizeSelect = (size) => {
    setShowPdfSizeModal(false);
    if (size === "Cover") {
      setShowCoverModal(true);
      return;
    }
    setPdfPaperSize(size);
    setShowPdfModal(true);
  };

  const isQuoteMode = useMemo(() => {
    if (!preparedData?.invoice) return false;
    const inv = preparedData.invoice;
    const docNo = String(
      inv.voucherNumber || inv.doc_number || "",
    ).toUpperCase();
    return docNo.startsWith("QSAL_") || getCurrentPinType() === "quote";
  }, [preparedData]);

  const PdfDocument = useMemo(() => {
    if (pdfPaperSize === "A5") {
      return isQuoteMode ? QuoteA5 : InvoicePDFA5;
    }
    return isQuoteMode ? QuoteInvoiceA4 : InvoicePDF;
  }, [pdfPaperSize, isQuoteMode]);

  const pdfContent = useMemo(() => {
    if (!preparedData?.invoice) return null;
    const invoiceData = normalizePdfInvoice(
      preparedData.invoice,
      preparedData.user,
      preparedData.bankAccounts,
    );

    return (
      <div style={{ height: "100%" }}>
        <PDFViewer
          width="100%"
          height="100%"
          key={`pdf-viewer-${preparedData.invoice.id}-${pdfPaperSize}`}
        >
          <PdfDocument invoiceData={invoiceData} />
        </PDFViewer>
      </div>
    );
  }, [preparedData, pdfPaperSize, PdfDocument]);

  const coverPdfContent = useMemo(() => {
    if (!preparedData?.invoice) return null;
    const customer = normalizePdfCustomer(
      preparedData.invoice,
      preparedData.user,
    );
    const coverData = {
      name: customer.name,
      address: customer.address,
      city: customer.city,
      mobile: customer.mobile,
      customer,
      invoice: preparedData.invoice,
    };
    return <InvoiceCoverPDF covers={[coverData]} />;
  }, [preparedData]);

  const handleDownloadPdfWithLetterPad = async () => {
    if (!preparedData?.invoice) return;
    try {
      const invoiceData = normalizePdfInvoice(
        preparedData.invoice,
        preparedData.user,
        preparedData.bankAccounts,
      );
      if (invoiceData.signed_qr_code) {
        invoiceData.signed_qr_image = await buildSignedQrDataUrl(
          invoiceData.signed_qr_code,
        );
      }
      const Doc =
        pdfPaperSize === "A5"
          ? isQuoteMode
            ? QuoteA5
            : InvoicePDFA5
          : isQuoteMode
            ? QuoteInvoiceA4
            : InvoicePDF;

      const blob = await pdf(
        <Doc
          invoiceData={invoiceData}
          embedLetterhead={pdfPaperSize === "A4"}
        />,
      ).toBlob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `invoice_${invoiceData.invoiceNumber || preparedData.invoice.id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("PDF download failed:", err);
      toast.error("Failed to download PDF");
    }
  };

  if (!show && !showPdfSizeModal && !showPdfModal && !showCoverModal) {
    return null;
  }

  return (
    <>
      {/* Select Invoice Size Modal */}
      <Modal
        show={showPdfSizeModal}
        onHide={handleCloseAll}
        centered
        size="sm"
      >
        <Modal.Header closeButton>
          <Modal.Title>Select Invoice Size</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loading ? (
            <div className="text-center py-4">
              <Spinner animation="border" variant="primary" />
              <p className="text-muted mt-2 mb-0">Loading invoice details...</p>
            </div>
          ) : (
            <>
              <p className="text-muted mb-3">
                Choose the paper size for the invoice preview.
              </p>
              <div className="d-grid gap-2">
                <Button
                  variant="outline-primary"
                  className={buttonClass}
                  onClick={() => handlePdfSizeSelect("A4")}
                >
                  A4 Size
                </Button>
                <Button
                  variant="outline-primary"
                  className={buttonClass}
                  onClick={() => handlePdfSizeSelect("A5")}
                >
                  A5 Size
                </Button>
                <Button
                  variant="outline-primary"
                  className={buttonClass}
                  onClick={() => handlePdfSizeSelect("Cover")}
                >
                  Cover
                </Button>
              </div>
            </>
          )}
        </Modal.Body>
      </Modal>

      {/* Invoice PDF Preview Modal */}
      <Modal
        show={showPdfModal}
        onHide={handleCloseAll}
        size="xl"
        centered
        dialogClassName="pdf-modal"
      >
        <Modal.Header className="d-flex align-items-center justify-content-between">
          <Modal.Title>Invoice PDF Preview</Modal.Title>
          <div className="d-flex align-items-center gap-2">
            {pdfPaperSize === "A4" && (
              <button
                type="button"
                className="btn border-0 bg-transparent p-0 shadow-none"
                onClick={handleDownloadPdfWithLetterPad}
                title="Download on Letter Pad"
                aria-label="Download on Letter Pad"
              >
                <Icon
                  icon="solar:download-minimalistic-bold-duotone"
                  width="22"
                  height="22"
                  style={{ color: "var(--theme-primary)" }}
                />
              </button>
            )}
            <button
              type="button"
              className="btn-close"
              aria-label="Close"
              onClick={handleCloseAll}
            />
          </div>
        </Modal.Header>
        <Modal.Body style={{ height: "80vh", overflow: "auto" }}>
          {pdfContent}
        </Modal.Body>
      </Modal>

      {/* Bill Cover Preview Modal */}
      <Modal
        show={showCoverModal}
        onHide={handleCloseAll}
        size="xl"
        centered
        dialogClassName="pdf-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Bill Cover Preview</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ height: "80vh", overflow: "auto" }}>
          {coverPdfContent}
        </Modal.Body>
      </Modal>
    </>
  );
}
